/**
 * The z-index scale, written down.
 *
 * Before this file there were 21 distinct z-index values across app/ and
 * components/ with nothing recording what sat above what. Raising the public
 * menu shell above the chat widget meant grepping the whole tree to reconstruct
 * the order by hand, and that reconstruction still missed `z-[1300]` (the
 * shadcn Select popover), which sits above the dialog layer. That is the exact
 * failure mode an undocumented scale produces: every change is archaeology, and
 * the archaeology is incomplete.
 *
 * Rules:
 *  - Import from here. Do not write a new arbitrary `z-[…]` in a component.
 *  - Values are spaced so a layer can be slotted between two without renumbering.
 *  - Adding a tier means adding it HERE first, with a sentence saying why it
 *    belongs above the thing beneath it.
 *
 * The ordering is a product decision, not an arbitrary ramp. Two invariants:
 *  1. A guest must always be able to reach the shell (their only navigation)
 *     over any persistent page furniture.
 *  2. A true modal - payment, 2FA, a confirm dialog - outranks the shell, so
 *     chrome never floats over a flow that demands a decision.
 */

export const Z = {
  /** In-flow decoration: badges, absolute overlays inside a card. */
  base: 10,
  /** Sticky sub-headers inside a scrolling panel. */
  sticky: 20,
  /** Persistent page furniture: mini-cart bar, guest-orders launcher, hints. */
  furniture: 40,
  /** Bottom sheets and inline popovers that dismiss on outside click. */
  sheet: 50,
  /** Cookie consent - above furniture, below anything the guest initiated. */
  consent: 70,
  /** Full-surface readers (flipbook, AI chat panel). */
  surface: 100,
  /** Onboarding tour spotlight + its cutout. */
  tour: 105,
  /** The public menu action bar (Cart / Discover / Orders). */
  actionBar: 110,
  /**
   * Docked assistant widgets (customer chat bubble). The *bubble* is
   * persistent furniture: the shell stays above it so the guest's chrome is
   * never buried. See `assistantOpen` for the expanded panel.
   */
  assistant: 1050,

  /**
   * Public menu shell. Above every persistent surface above, because it is the
   * guest's only way out of whatever is covering the screen.
   */
  shellBackdrop: 1060,
  shellChrome: 1070,

  /**
   * Open AI chat panel. On phones the panel is full-surface, so its header ,
   * which carries the Close control , must clear the shell's 56px chrome,
   * or the guest cannot dismiss the chat (the shell sits on top of the X).
   * The moment the panel opens it takes this tier, above the shell but still
   * below true modals (`checkout`), which must never be buried by chrome.
   */
  /**
   * The assistant's ambient presence layer — the edge glow that shows it is
   * listening, thinking or speaking (doc 64 §11.2).
   *
   * ABOVE `shellChrome` because it frames the whole screen, including the
   * header, and a glow that stopped at the chrome would read as a rendering
   * bug rather than an ambient state. BELOW `assistantOpen` so the panel is
   * never tinted by it. It is `pointer-events: none` and purely decorative:
   * nothing is ever reachable only through this layer.
   */
  assistantAmbient: 1074,

  assistantOpen: 1075,

  /**
   * The guest's cart, expanded over the place.
   *
   * IT NEEDS ITS OWN TIER because it is a guest-initiated overlay that must
   * clear the shell chrome (1070) but stay under a true modal (1080). It had
   * no tier, so its two halves were written by hand and drifted apart: the
   * backdrop was `z-[1075]` while the panel used `sheet` (50). A blurring
   * backdrop painted 1000 layers ABOVE the panel it was meant to sit behind,
   * so the entire cart — every line item, the total, and the button that
   * places the order — rendered dimmed and out of focus. The most important
   * screen in the product was unreadable, and no test caught it because both
   * elements were individually "correct".
   */
  cartBackdrop: 1076,
  cartSheet: 1078,

  /**
   * True modals. Deliberately ABOVE the shell: chrome must never float over a
   * payment flow or a security challenge.
   */
  checkout: 1080,
  dialogBackdrop: 1200,
  dialogContent: 1210,

  /**
   * Popovers launched FROM a dialog (shadcn Select menus). Must clear
   * `dialogContent` or a select inside a modal renders behind it.
   */
  popoverOverDialog: 1300,
} as const;

export type ZLayer = keyof typeof Z;

/**
 * Literal Tailwind classes per layer, written out so the Tailwind scanner can
 * see them. `zClass()` must NOT build these at runtime (`z-[${Z[layer]}]`):
 * Tailwind only emits arbitrary-value utilities whose exact class string
 * appears in a scanned source file, so a value that only exists as a number
 * here (e.g. `checkout` -> `z-[1080]`) would silently produce no CSS rule and
 * the layer would collapse into DOM order. Every tier has a literal entry, and
 * the `satisfies Record<ZLayer, string>` check below keeps this list in sync
 * with the scale.
 */
const Z_CLASS = {
  base: 'z-[10]',
  sticky: 'z-[20]',
  furniture: 'z-[40]',
  sheet: 'z-[50]',
  consent: 'z-[70]',
  surface: 'z-[100]',
  tour: 'z-[105]',
  actionBar: 'z-[110]',
  assistant: 'z-[1050]',
  assistantAmbient: 'z-[1074]',
  assistantOpen: 'z-[1075]',
  cartBackdrop: 'z-[1076]',
  cartSheet: 'z-[1078]',
  shellBackdrop: 'z-[1060]',
  shellChrome: 'z-[1070]',
  checkout: 'z-[1080]',
  dialogBackdrop: 'z-[1200]',
  dialogContent: 'z-[1210]',
  popoverOverDialog: 'z-[1300]',
} as const satisfies Record<ZLayer, string>;

/** Tailwind class for a layer: zClass('shellChrome') -> 'z-[1070]'. */
export function zClass(layer: ZLayer): string {
  return Z_CLASS[layer];
}
